import React, { useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useAuth } from '../../context/AuthContext';
import { useAuthPage } from '../../hooks/useAuthPage';
import { loginPathFor, rememberReturnPath } from '../../context/signedInState';
import { FlowHeader } from '../account/TinycmsSignupFlow';
import { uploadAsset } from '../../lib/media/uploadAsset';
import { isVideoFile, uploadVideo } from '../../lib/media/uploadVideo';
import { Loader } from '../common/PageLoader';
import { apiFetch } from '../../lib/apiFetch';

/**
 * The page behind an agent's request_media_upload link: the person uploads
 * photos or videos from their phone or computer, and the agent reads them back
 * by request id. Videos go straight to video hosting, in resumable pieces.
 * Signed in, as a member of the request's site (the server checks every
 * action); a signed-out visitor goes to sign-in and comes straight back here.
 * Laid out like sign-in (ink-flow.css .flow): people often open the link on a
 * phone, straight from a chat.
 */

const REQUEST_FIELDS = `
  id
  accountId
  accountName
  note
  maxFiles
  status
  expiresAt
  assets { assetId filename url kind status }
`;
const GET_REQUEST = gql`
  query MediaUploadRequest($token: String!) {
    mediaUploadRequest(token: $token) { ${REQUEST_FIELDS} }
  }
`;
const ADD = gql`
  mutation AddToMediaUploadRequest($token: String!, $assetId: ID!) {
    addToMediaUploadRequest(token: $token, assetId: $assetId) { ${REQUEST_FIELDS} }
  }
`;
const FINISH = gql`
  mutation FinishMediaUploadRequest($token: String!) {
    finishMediaUploadRequest(token: $token) { ${REQUEST_FIELDS} }
  }
`;

interface RequestData {
  id: string;
  accountId: string;
  accountName: string;
  note?: string | null;
  maxFiles: number;
  status: 'open' | 'done' | 'expired';
  expiresAt: string;
  assets: Array<{
    assetId: string;
    filename?: string | null;
    url?: string | null;
    kind?: string | null;
    status?: string | null;
  }>;
}

interface Pending {
  key: string;
  name: string;
  preview: string | null;
  state: 'uploading' | 'failed';
  error?: string;
  percent?: number;
  /** What the tile says when there's no picture to show. */
  label?: string;
}

const MAX_BYTES = 24 * 1024 * 1024;
/** What the picker offers: the Media screen's list, less fonts. */
const ACCEPT = 'image/*,audio/*,video/*,.gltf,.glb,.bin,.ktx2';
const isGltf = (f: File) => /\.gltf$/i.test(f.name);
const isGlb = (f: File) => /\.glb$/i.test(f.name);
/** A .gltf's own files: buffers and KTX2 textures (PNG/JPEG/WebP go by what it names). */
const isModelPart = (f: File) => /\.(bin|ktx2)$/i.test(f.name);
const isTexture = (f: File) => /\.(png|jpe?g|webp)$/i.test(f.name);
const baseName = (name: string) => name.split(/[\\/]/).pop() || name;

/**
 * The files a .gltf names (its buffers and images), matched the way the
 * server matches them (media/model.js planModelSet): the decoded address, or
 * its last part, against each file's name. Null when it can't be read; the
 * server then says what's wrong.
 */
async function filesNamedBy(gltf: File): Promise<string[] | null> {
  try {
    const doc = JSON.parse(await gltf.text());
    const uris = [...(doc.buffers || []), ...(doc.images || [])]
      .map((x: { uri?: unknown }) => x?.uri)
      .filter((u: unknown): u is string => typeof u === 'string' && !!u && !u.startsWith('data:'))
      .map((u: string) => {
        try {
          return decodeURIComponent(u);
        } catch {
          return u;
        }
      });
    return Array.from(new Set(uris));
  } catch {
    return null;
  }
}
const fileFor = (files: File[], ref: string) =>
  files.find((f) => f.name === ref) || files.find((f) => f.name === baseName(ref));

/** A .gltf's set while some of its files are still to be chosen. */
interface Waiting {
  gltf: string | null;
  missing: string[];
  count: number;
}

export function UploadRequestPage() {
  // The app locks the document's scroll; this page scrolls like sign-in.
  useAuthPage();
  const { token = '' } = useParams();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { data, loading, error, refetch } = useQuery<{ mediaUploadRequest: RequestData }>(
    GET_REQUEST,
    { variables: { token }, skip: !isAuthenticated, fetchPolicy: 'network-only' }
  );
  const [add] = useMutation(ADD);
  const client = useApolloClient();
  const [finish, { loading: finishing }] = useMutation(FINISH);
  const [pending, setPending] = useState<Pending[]>([]);
  // A 3D model's files, gathered over one or more picks until the .gltf and
  // every file it names are here; then they go up as one upload.
  const [held, setHeld] = useState<File[]>([]);
  const [waiting, setWaiting] = useState<Waiting | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  if (isLoading)
    return (
      <Shell>
        <Loader />
      </Shell>
    );
  if (!isAuthenticated) {
    // Back here after signing in, even from a restored tab (signedInState).
    rememberReturnPath(location.pathname);
    return (
      <Navigate to={loginPathFor(location.pathname)} replace state={{ from: location.pathname }} />
    );
  }
  if (loading && !data)
    return (
      <Shell>
        <Loader />
      </Shell>
    );
  if (error || !data) {
    const msg = error?.graphQLErrors?.[0]?.message || 'This upload link didn’t open.';
    return (
      <Shell>
        <span className="upr-state" data-tone="error">
          <AlertIcon />
        </span>
        <h1 className="flow-h1">This link didn’t open</h1>
        <p className="flow-lede">{msg}</p>
        <p className="upr-aside">
          Uploads go to a site, so you need to be signed in as someone on it. Ask your agent for a
          new link if this one is old.
        </p>
      </Shell>
    );
  }

  const req = data.mediaUploadRequest;
  // A model still being gathered holds its place: it will be one file.
  const used =
    req.assets.length +
    pending.filter((p) => p.state === 'uploading').length +
    (held.length ? 1 : 0);
  const left = Math.max(0, req.maxFiles - used);

  const fail = (key: string, err: unknown) =>
    setPending((p) =>
      p.map((x) =>
        x.key === key
          ? { ...x, state: 'failed', error: err instanceof Error ? err.message : 'Upload failed' }
          : x
      )
    );

  // A 3D model (the .gltf and the files it names, or one .glb) is one upload
  // and counts as one file, however many files it has (as on Media).
  const sendModel = async (files: File[]) => {
    const entry = files.find((f) => isGltf(f) || isGlb(f)) || files[0];
    const key = `model-${entry.name}-${Math.random()}`;
    const name = files.length > 1 ? `${entry.name} + ${files.length - 1} files` : entry.name;
    setPending((p) => [...p, { key, name, preview: null, label: '3D model', state: 'uploading' }]);
    try {
      const form = new FormData();
      form.append('accountId', req.accountId);
      for (const f of files) form.append('file', f, f.name);
      const res = await apiFetch('/api/upload/model', { method: 'POST', body: form });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.assetId) throw new Error(out.error || `Upload failed (${res.status})`);
      await add({ variables: { token, assetId: out.assetId } });
      await refetch();
      setPending((p) => p.filter((x) => x.key !== key));
    } catch (err) {
      fail(key, err);
    }
  };

  /**
   * Add this pick's model files to the ones held, then send the model if the
   * .gltf and everything it names are here; otherwise keep waiting and say
   * what's still to choose. Returns the pick's other files (photos and so on),
   * less any textures the .gltf names.
   */
  const gatherModel = async (files: File[]): Promise<File[]> => {
    const picked = files.filter((f) => isGltf(f) || isModelPart(f));
    let rest = files.filter((f) => !isGltf(f) && !isModelPart(f));
    if (!picked.length && !held.length) return rest;
    if (!held.length && left === 0) return rest; // The link is full.
    // The newest file of a name wins, so choosing a file again replaces it.
    const byName = new Map(held.map((f) => [f.name, f]));
    for (const f of picked) byName.set(f.name, f);
    let gathered = Array.from(byName.values());
    const gltf = gathered.find(isGltf);
    if (!gltf) {
      setHeld(gathered);
      setWaiting({ gltf: null, missing: [], count: gathered.length });
      return rest;
    }
    const names = await filesNamedBy(gltf);
    if (names) {
      const textures = rest.filter(isTexture).filter((f) => names.some((r) => fileFor([f], r)));
      rest = rest.filter((f) => !textures.includes(f));
      gathered = gathered.concat(textures);
    }
    const missing = names ? names.filter((r) => !fileFor(gathered, r)) : [];
    if (missing.length) {
      setHeld(gathered);
      setWaiting({ gltf: gltf.name, missing, count: gathered.length });
      return rest;
    }
    // Only the files the .gltf uses go up; anything else held is dropped.
    const set = names
      ? [gltf, ...Array.from(new Set(names.map((r) => fileFor(gathered, r)!)))]
      : gathered;
    setHeld([]);
    setWaiting(null);
    void sendModel(set);
    return rest;
  };

  const sendFiles = async (all: File[]) => {
    // A .glb holds everything: each one is a model on its own.
    for (const glb of all.filter(isGlb).slice(0, left)) void sendModel([glb]);
    const files = await gatherModel(all.filter((f) => !isGlb(f)));
    const take = files
      .filter(
        (f) =>
          f.type.startsWith('image/') ||
          f.type.startsWith('audio/') ||
          /\.heic$/i.test(f.name) ||
          isVideoFile(f)
      )
      .slice(0, left);
    for (const file of take) {
      const key = `${file.name}-${file.size}-${Math.random()}`;
      const video = isVideoFile(file);
      const item: Pending = {
        key,
        name: file.name,
        preview: video ? null : URL.createObjectURL(file),
        state: 'uploading',
      };
      setPending((p) => [...p, item]);
      try {
        let assetId: string;
        if (video) {
          ({ assetId } = await uploadVideo(client, req.accountId, file, (percent) =>
            setPending((p) => p.map((x) => (x.key === key ? { ...x, percent } : x)))
          ));
        } else {
          if (file.size > MAX_BYTES) throw new Error('Larger than 24 MB');
          ({ assetId } = await uploadAsset(req.accountId, file, { filename: file.name }));
        }
        await add({ variables: { token, assetId } });
        await refetch();
        setPending((p) => p.filter((x) => x.key !== key));
        if (item.preview) URL.revokeObjectURL(item.preview);
      } catch (err) {
        fail(key, err);
      }
    }
  };

  if (req.status !== 'open') {
    const done = req.status === 'done';
    return (
      <Shell>
        <span className="upr-state" data-tone={done ? 'ok' : 'muted'}>
          {done ? <CheckIcon /> : <ClockIcon />}
        </span>
        <h1 className="flow-h1">{done ? 'All set' : 'This link has expired'}</h1>
        <p className="flow-lede">
          {done
            ? `${req.assets.length} ${req.assets.length === 1 ? 'file is' : 'files are'} with your agent. Go back to it and tell it you’re done.`
            : 'Ask your agent for a new upload link.'}
        </p>
        <Grid assets={req.assets} pending={[]} />
      </Shell>
    );
  }

  const uploading = pending.some((p) => p.state === 'uploading');
  return (
    <Shell>
      <p className="upr-eyebrow">For {req.accountName}</p>
      <h1 className="flow-h1">Add photos or videos for your agent</h1>
      <p className="flow-lede">
        They go to the site’s media library, where your agent picks them up.
      </p>
      {req.note ? (
        <figure className="upr-note">
          <figcaption>Your agent asked</figcaption>
          <blockquote>{req.note}</blockquote>
        </figure>
      ) : null}

      <div
        className="upr-drop"
        data-dragging={dragging ? 'true' : undefined}
        data-full={left === 0 ? 'true' : undefined}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void sendFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <span className="upr-drop-icon">
          <UploadIcon />
        </span>
        <button
          type="button"
          className="upr-choose"
          disabled={left === 0}
          onClick={() => input.current?.click()}
        >
          Choose files
        </button>
        <p className="upr-drop-hint">
          {left === 0 ? (
            `That’s the ${req.maxFiles} this link takes.`
          ) : (
            <>
              <span className="upr-drop-only">or drop them here · </span>up to {left} more
            </>
          )}
        </p>
        <p className="upr-drop-limits">
          Photos up to 24 MB · videos up to 4 GB · 3D models up to 100 MB
        </p>
        <p className="upr-drop-limits">
          A 3D model counts as one file, however many files it has. Choose its .gltf and the files
          it uses (all at once or a few at a time), or one .glb.
        </p>
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            void sendFiles(Array.from(e.target.files || []));
            e.target.value = '';
          }}
        />
      </div>

      {req.assets.length || pending.length ? (
        <div className="upr-count">
          <span>
            {req.assets.length} of {req.maxFiles} added
          </span>
          {uploading ? <span className="upr-count-busy">Uploading…</span> : null}
        </div>
      ) : null}
      {waiting ? (
        <div className="upr-wait" role="status">
          <div className="upr-wait-head">
            <span className="upr-wait-title">3D model · waiting for files</span>
            <button
              type="button"
              className="flow-link"
              onClick={() => {
                setHeld([]);
                setWaiting(null);
              }}
            >
              Start over
            </button>
          </div>
          {waiting.gltf ? (
            <p>
              {waiting.gltf} uses{' '}
              {waiting.missing.length === 1
                ? 'one more file'
                : `${waiting.missing.length} more files`}
              . Choose {waiting.missing.length === 1 ? 'it' : 'them'} too; nothing goes up until
              they’re all here.
            </p>
          ) : (
            <p>
              {waiting.count} {waiting.count === 1 ? 'file' : 'files'} of a 3D model so far. Choose
              its .gltf file too. If you already uploaded its .gltf, these files went up with it and
              there’s nothing more to do.
            </p>
          )}
          {waiting.missing.length ? (
            <ul>
              {waiting.missing.slice(0, 6).map((m) => (
                <li key={m}>{m}</li>
              ))}
              {waiting.missing.length > 6 ? <li>and {waiting.missing.length - 6} more</li> : null}
            </ul>
          ) : null}
        </div>
      ) : null}
      <Grid assets={req.assets} pending={pending} />

      <button
        type="button"
        className="flow-btn"
        disabled={!req.assets.length || finishing || uploading || !!waiting}
        onClick={async () => {
          await finish({ variables: { token } });
          await refetch();
        }}
      >
        {finishing ? 'Finishing…' : 'I’m done'}
      </button>
      <p className="flow-fine">Then tell your agent. It picks them up from here.</p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flow upr">
      <FlowHeader />
      <main className="flow-main upr-main">
        <section className="upr-section">{children}</section>
      </main>
    </div>
  );
}

function Grid({ assets, pending }: { assets: RequestData['assets']; pending: Pending[] }) {
  if (!assets.length && !pending.length) return null;
  return (
    <ul className="upr-grid">
      {assets.map((a) => (
        <li key={a.assetId} className="upr-tile" data-state="done">
          <div className="upr-thumb">
            {/* A sound file's or a model's address isn't a picture. */}
            {a.url && a.kind !== 'audio' && a.kind !== 'model' ? (
              <img src={a.url} alt="" />
            ) : (
              <span className="upr-noimg">
                {a.kind === 'video'
                  ? 'Video — getting it ready'
                  : a.kind === 'audio'
                    ? 'Sound file'
                    : a.kind === 'model'
                      ? '3D model'
                      : 'No preview'}
              </span>
            )}
            <span className="upr-badge" aria-label="Uploaded">
              <CheckIcon />
            </span>
          </div>
          <div className="upr-caption">{a.filename || 'Uploaded'}</div>
        </li>
      ))}
      {pending.map((p) => (
        <li key={p.key} className="upr-tile" data-state={p.state}>
          <div className="upr-thumb">
            {p.preview ? (
              <img src={p.preview} alt="" />
            ) : (
              <span className="upr-noimg">{p.label || 'Video'}</span>
            )}
            {p.state === 'uploading' ? (
              <span className="upr-bar" data-indeterminate={p.percent == null ? 'true' : undefined}>
                <span style={p.percent != null ? { width: `${p.percent}%` } : undefined} />
              </span>
            ) : null}
          </div>
          <div className="upr-caption">
            {p.state === 'uploading'
              ? `Uploading${p.percent != null ? ` ${p.percent}%` : '…'}`
              : `Didn’t upload: ${p.error}`}
          </div>
        </li>
      ))}
    </ul>
  );
}

const ICON = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function UploadIcon() {
  return (
    <svg {...ICON}>
      <path d="M12 15V4M7.5 8.5 12 4l4.5 4.5" />
      <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...ICON}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5.5M12 16.5v.01" />
    </svg>
  );
}
